import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { SecondaryText } from 'Components/Texts';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { GIFTING_QUERY_PARAM, VA_QUERY_PARAM } from 'Utils/Constants';
import { checkIfShowSheerBanner, isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { SheerIdBanner } from 'Features/SheerIdPromoBanner/SheerIdBanner';
import { getSheerProgramFromSKU } from 'Utils/SheerIdHelpers';
import { createUserAccount, handleSignInSuccess, handleSignUpSuccess } from 'Utils/SignInHelpers';
import CreateAccountForm from 'Forms/CreateAccountForm';
import './CreateContainer.css';
import { getPlans } from 'State/Plans/PlansActions';

export const CreateContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { preSelectedPlan = '' } = useParams();

  const { hasSubscription, isFetching, isStatusSuccess, isIAPUser, hasActiveSubscription } = useSelector(
    (s) => s.subscription
  );

  const { routes, defaultConfig } = useSelector((s) => s.appConfig);
  const user = useSelector((s) => s.user);
  const { bango, sheerId } = useSelector((s) => s.integrations);
  const pageStartSource = useSelector((s) => s.tracking.pageStartSource);
  const { requirePlanSelection, isGifting } = useSelector((s) => s.purchaseMode);
  const { hasShippingAddress } = useSelector((s) => s.checkout);
  const { isPlanSelected, selectedPlan, selectPlanActionComplete } = useSelector((s) => s.plans);

  const { verificationId } = sheerId;
  const isSheerIdVerification = (location?.state?.from?.pathname || '').includes(routes.sheerIdVerification);
  const isBango = bango.subscriptionId;

  //This is used so the redirects don't trigger after account creation on this page
  const isAuthenticatedOnFirstRender = useRef(user.isAuthenticated);

  const isLoading =
    (isFetching && !isStatusSuccess) || (requirePlanSelection && !selectPlanActionComplete && !isPlanSelected);

  const urlParams = new URLSearchParams(location.search);

  const bangoProductId = bango.productId;
  const sheerProgramType = urlParams.get('sheerVerification') || getSheerProgramFromSKU(bangoProductId);
  const isVeteransAdv = urlParams.get(VA_QUERY_PARAM);

  useEffect(() => {
    if (!isLoading && !user.isAuthenticated) {
      trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_STARTED);
    }
  }, [isLoading, user.isAuthenticated]);

  useEffect(() => {
    dispatch(getPlans(preSelectedPlan));
  }, [dispatch, preSelectedPlan]);

  if (isAuthenticatedOnFirstRender.current && isStatusSuccess && hasSubscription && !isBango) {
    if (verificationId) {
      if (isIAPUser && hasActiveSubscription) {
        return (
          <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriberMilitaryFirst }} />
        );
      }
      const param = preSelectedPlan ? '/' + preSelectedPlan : '';
      return <Navigate replace to={{ search: location.search, pathname: routes.changePlans + param }} />;
    }
    return <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriber }} />;
  }

  if (selectedPlan?.hasMerchandise && !hasShippingAddress) {
    return <Navigate replace to={routes.shipping} />;
  }

  if (
    requirePlanSelection &&
    selectPlanActionComplete &&
    !isPlanSelected &&
    !preSelectedPlan &&
    !isBango &&
    !isSheerIdVerification &&
    !isVeteransAdv
  ) {
    return <Navigate replace to={routes.signupPlans} />;
  }

  if (isAuthenticatedOnFirstRender.current && selectPlanActionComplete) {
    if (isPlanSelected && bango.subscriptionId) {
      return <Navigate replace to={routes.bangoFinal} />;
    } else if (isPlanSelected) {
      return <Navigate replace to={routes.payment} />;
    } else if (!bango.subscriptionId && !sheerProgramType) {
      return <Navigate replace to={routes.signupPlans} />;
    } else if (sheerProgramType) {
      return <Navigate replace to={`${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`} />;
    }
  }

  let searchParam;
  if (isGifting) {
    searchParam = `${GIFTING_QUERY_PARAM}`;
  } else if (isVeteransAdv) {
    searchParam = `${VA_QUERY_PARAM}=${isVeteransAdv}`;
  } else {
    searchParam = `return_to=${location.pathname}${location.search}`; //create account screen including params
  }

  let title = 'Checking your subscription';
  if (!isLoading) {
    title = isGifting ? 'Create Your Account To Redeem' : 'Create Your Account';
  }

  const { enableSheerIdBanner } = defaultConfig;
  const showBanner = (isDefaultPlanFlow() && !isBango && !isGifting && enableSheerIdBanner) || checkIfShowSheerBanner();

  const onSignInSuccess = handleSignInSuccess(location.pathname);
  const onSignUpSuccess = handleSignUpSuccess(isSheerIdVerification, isVeteransAdv, urlParams, (path) => {
    navigate(path);
  });

  return (
    <CenterFrameLayout
      title={title}
      showLoader={isLoading}
      modernLayout
      customFrameClass={showBanner && 'frameWithSheerIdBanner'}
    >
      {showBanner && <SheerIdBanner />}
      <CreateAccountForm
        submitButtonText="Continue"
        pageStartSource={pageStartSource}
        onFormSubmit={(values) => createUserAccount(values, onSignInSuccess, onSignUpSuccess)}
      />
      <SecondaryText
        key="b"
        alignText="centerText"
        text={[
          'Already have an account? ',
          <Link
            key="0"
            to={{
              pathname: routes.signin,
              search: searchParam,
            }}
            className="blueLink"
          >
            Sign in
          </Link>,
        ]}
      />
    </CenterFrameLayout>
  );
};
